import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useMe, useUpdateUiPrefs } from '@/features/auth/hooks';
import { TOURS } from './tours';
import {
  buildToursPatch,
  resolvePresentSteps,
  resumeIndex,
  toursFromMe,
  type ActiveTour,
} from './state';
import type { TourId, TourStatus } from './types';

interface TourContextValue {
  active: ActiveTour | null;
  /** N in "X of N" — the present-step count for the running tour. */
  total: number;
  /** X in "X of N" — 1-based position (0 when idle). */
  position: number;
  /** Tour ID requested for start (e.g. via replay) but not yet begun —
   *  consumed by the target page's `useTourTrigger` once ready. */
  pendingTourId: TourId | null;
  start: (tourId: TourId) => void;
  /** Record intent to start a tour; the caller navigates to the tour's page,
   *  whose `useTourTrigger` consumes the request once ready. */
  requestStart: (tourId: TourId) => void;
  next: () => void;
  prev: () => void;
  close: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

function targetPresent(target: string): boolean {
  return document.querySelector(`[data-tour="${target}"]`) !== null;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const updatePrefs = useUpdateUiPrefs();
  const [active, setActive] = useState<ActiveTour | null>(null);
  const [pendingTourId, setPendingTourId] = useState<TourId | null>(null);

  // End a page-scoped tour when the user navigates away. The scrim is
  // pointer-events-none so a highlighted nav link stays clickable, and Back
  // also leaves the page — without this, the root-level overlay plus its global
  // key/focus capture would orphan onto the next page. "Seen" is already
  // recorded by `start`, so no write is needed here.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeRef = useRef(active);
  activeRef.current = active;
  useEffect(() => {
    if (activeRef.current) setActive(null);
    // Fire only on route change; `active` is read via ref so per-step updates
    // (which change `active`) don't re-run this and end the tour prematurely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const persist = useCallback(
    (tourId: TourId, status: TourStatus, stepId: string) => {
      updatePrefs.mutate(buildToursPatch(toursFromMe(me), tourId, { status, step: stepId }));
    },
    [me, updatePrefs],
  );

  const start = useCallback(
    (tourId: TourId) => {
      setPendingTourId(null);
      const steps = resolvePresentSteps(TOURS[tourId], targetPresent);
      if (steps.length === 0) return;
      const entry = toursFromMe(me)[tourId];
      const savedStep = entry?.status === 'completed' ? null : (entry?.step ?? null);
      const index = resumeIndex(steps, savedStep);
      setActive({ tourId, steps, index });
      persist(tourId, 'in_progress', steps[index]!.id);
    },
    [me, persist],
  );

  const requestStart = useCallback((tourId: TourId) => {
    setPendingTourId(tourId);
  }, []);

  // Persistence is deliberately minimal (a load test showed the ui_prefs write
  // path is the scaling pinch point at mass-onboarding peaks). The ONLY writes
  // are `start` — which records "seen" so the tour never auto-fires again — and
  // `close` (dismissed), so a dismissed tour resumes where the user left off.
  // Stepping and *finishing* the tour write nothing: `start` already marked it
  // seen, and a finished tour replays as a restart from step 1 regardless. So a
  // user who completes the tour makes exactly one tiny write, ever.
  const next = useCallback(() => {
    if (!active) return;
    if (active.index >= active.steps.length - 1) {
      setActive(null);
      return;
    }
    setActive({ ...active, index: active.index + 1 });
  }, [active]);

  const prev = useCallback(() => {
    if (!active || active.index === 0) return;
    setActive({ ...active, index: active.index - 1 });
  }, [active]);

  const close = useCallback(() => {
    if (!active) return;
    persist(active.tourId, 'dismissed', active.steps[active.index]!.id);
    setActive(null);
  }, [active, persist]);

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      total: active?.steps.length ?? 0,
      position: active ? active.index + 1 : 0,
      pendingTourId,
      start,
      requestStart,
      next,
      prev,
      close,
    }),
    [active, pendingTourId, start, requestStart, next, prev, close],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTourContext(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTourContext must be used within <TourProvider>');
  return ctx;
}
